import { useContext, useEffect, useRef, useState } from 'react'

import { Group, Menu, Modal, Textarea, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import MentionHighlightBackdrop from 'src/components/Inputs/MentionHighlightBackdrop/MentionHighlightBackdrop'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import {
  CREATE_NOTE_TEMPLATE,
  UPDATE_NOTE_TEMPLATE,
} from 'src/graphql/noteTemplates.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import { NOTE_MAX_LENGTH } from 'src/lib/notes'
import { focusCaretAfterInsert, insertAtCaret } from 'src/lib/textInput.utils'
import { TextInputMentions } from 'src/lib/textInputMentions'
import {
  NOTE_MENTION_VARIABLES,
  NOTE_TEMPLATE_VARIABLES,
} from 'src/lib/textTemplateVariables'

const TITLE_MAX_LENGTH = 100

const MENTION_VARIABLE_VALUES = new Set(
  NOTE_MENTION_VARIABLES.map((variable) => variable.value)
)

interface MentionOption {
  key: string
  label: string
  text: string
}

const schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: 'Please include a template name' })
    .max(TITLE_MAX_LENGTH, { message: 'Template name is too long' }),
  content: z
    .string()
    .trim()
    .min(1, { message: 'Please include the note text' })
    .max(NOTE_MAX_LENGTH, {
      message: `Character limit is ${NOTE_MAX_LENGTH}.`,
    }),
})

interface NoteTemplateFormValues {
  title: string
  content: string
}

interface CreateNoteTemplateModalProps {
  noteTemplate?: { noteTemplateId: string; title: string; content: string }
  isOpen: boolean
  onClose: () => void
  refetchQueries: unknown[]
  edit?: boolean
}

const CreateNoteTemplateModal = ({
  noteTemplate,
  isOpen,
  refetchQueries,
  onClose,
  edit = false,
}: CreateNoteTemplateModalProps) => {
  const contentRef = useRef<HTMLTextAreaElement>(null)
  const recruiters = useContext(RecruitersContext) || []
  const [cursorPosition, setCursorPosition] = useState(0)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  // Set by programmatic inserts so the @ picker does not reopen on them.
  const skipMentionPickerRef = useRef(false)

  const form = useForm<NoteTemplateFormValues>({
    initialValues: {
      title: noteTemplate?.title || '',
      content: noteTemplate?.content || '',
    },
    validate: zodResolver(schema),
  })

  // Reset on every open: create → close → create keeps the same (undefined)
  // template prop, so keying on the prop alone would leave the last draft
  // and its validation errors in place.
  useEffect(() => {
    if (!isOpen) return
    form.setValues({
      title: noteTemplate?.title || '',
      content: noteTemplate?.content || '',
    })
    form.resetDirty()
    form.clearErrors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, noteTemplate])

  const [updateNoteTemplate, { loading: updating }] = useMutation(
    UPDATE_NOTE_TEMPLATE,
    {
      refetchQueries,
      onCompleted: () => {
        toast('Your note template was updated successfully', 'success')
        onClose()
      },
      onError: () => {
        toast('Unable to update note template, please try again.', 'error')
      },
    }
  )

  const [createNoteTemplate, { loading: creating }] = useMutation(
    CREATE_NOTE_TEMPLATE,
    {
      refetchQueries,
      onCompleted: () => {
        toast('A new note template was created successfully', 'success')
        onClose()
      },
      onError: () => {
        toast('Unable to create note template, please try again.', 'error')
      },
    }
  )

  useEffect(() => {
    if (skipMentionPickerRef.current) {
      skipMentionPickerRef.current = false
      setMentionQuery(null)
      return
    }
    const content = String(form.values.content ?? '')
    setMentionQuery(
      TextInputMentions.getTriggerQuery(
        content,
        Math.min(cursorPosition, content.length)
      )
    )
  }, [form.values.content, cursorPosition])

  // Mentions are followed by a space so the next one cannot run into them;
  // the note composer turns `@First Last` into an employee tag on save.
  const insertAtCursor = (
    text: string,
    { mention = false, replaceTrigger = false } = {}
  ) => {
    const current = String(form.values.content ?? '')
    const { content, caret } = replaceTrigger
      ? TextInputMentions.replaceAtTrigger(contentRef.current, current, text)
      : insertAtCaret(contentRef.current, current, text)
    const pad = mention && !/\s/.test(content.charAt(caret))
    const next = pad
      ? `${content.slice(0, caret)} ${content.slice(caret)}`
      : content
    const nextCaret = mention ? caret + 1 : caret
    skipMentionPickerRef.current = true
    form.setFieldValue('content', next)
    setCursorPosition(nextCaret)
    focusCaretAfterInsert(contentRef.current, nextCaret)
  }

  const insertVariable = (token: string) =>
    insertAtCursor(`{{${token}}}`, {
      mention: MENTION_VARIABLE_VALUES.has(token),
    })

  const insertMention = (name: string) =>
    insertAtCursor(`@${name}`, { mention: true })

  const mentionOptions = (
    query: string
  ): { group: string; options: MentionOption[] }[] => {
    const matches = (option: MentionOption) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    return [
      {
        group: 'Assignees',
        options: NOTE_MENTION_VARIABLES.map((variable) => ({
          key: variable.value,
          label: variable.name,
          text: `{{${variable.value}}}`,
        })).filter(matches),
      },
      {
        group: 'Employees',
        options: recruiters
          .map((recruiter) => ({
            key: recruiter.value,
            label: recruiter.label,
            text: `@${recruiter.label}`,
          }))
          .filter(matches),
      },
    ].filter(({ options }) => options.length > 0)
  }

  const trackCaret = (event: { currentTarget: HTMLTextAreaElement }) =>
    setCursorPosition(event.currentTarget.selectionStart ?? 0)

  const onFormSubmit = (values: NoteTemplateFormValues) => {
    const input = { title: values.title.trim(), content: values.content.trim() }

    if (edit && noteTemplate) {
      updateNoteTemplate({
        variables: { noteTemplateId: noteTemplate.noteTemplateId, input },
      })
      return
    }

    createNoteTemplate({ variables: { input } })
  }

  const contentLength = form.values.content.length

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size="lg"
      classNames={{ title: 'text-lg font-bold' }}
      title={edit ? 'Edit Note Template' : 'Create Note Template'}
      centered
    >
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <div className="flex flex-col gap-6">
          <TextInput
            label="Template name"
            placeholder="Left voicemail"
            data-testid="note-template-title-input"
            {...form.getInputProps('title')}
          />
          <div className="flex flex-col gap-3">
            <div className="text-sm font-medium text-doubleNickel-gray-900">
              Click a variable to insert it at the cursor
            </div>
            <div className="flex flex-row flex-wrap gap-2">
              {NOTE_TEMPLATE_VARIABLES.map((variable) => (
                <StyledBadge
                  key={variable.value}
                  textTransform="capitalize"
                  onClick={() => insertVariable(variable.value)}
                >
                  {variable.name}
                </StyledBadge>
              ))}
              <StyledMenu shadow="md" width={240}>
                <Menu.Target>
                  {/* Wrapper forwards the ref Menu.Target needs; Enter and
                      Space reach the click handler Menu.Target clones on. */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Insert an employee mention"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        event.currentTarget.click()
                      }
                    }}
                  >
                    <StyledBadge
                      clickable
                      textTransform="capitalize"
                      rightSection={<IconChevronDown className="h-3 w-3" />}
                    >
                      Employees
                    </StyledBadge>
                  </div>
                </Menu.Target>
                <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                  {recruiters.length === 0 && (
                    <Menu.Item disabled>No employees found</Menu.Item>
                  )}
                  {recruiters.map((recruiter) => (
                    <Menu.Item
                      key={recruiter.value}
                      onClick={() => insertMention(recruiter.label)}
                    >
                      {recruiter.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </StyledMenu>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <StyledMenu opened={mentionQuery !== null} width={240}>
              <Menu.Target>
                <div className="relative">
                  <MentionHighlightBackdrop
                    content={String(form.values.content ?? '')}
                    recruiters={recruiters}
                    textareaRef={contentRef}
                  />
                  <Textarea
                    ref={contentRef}
                    label="Note"
                    placeholder="Write the note here... Type @ to mention someone."
                    autosize
                    minRows={5}
                    maxRows={10}
                    data-testid="note-template-content-input"
                    className="relative z-10"
                    classNames={{ input: '!bg-transparent' }}
                    {...form.getInputProps('content')}
                    onChange={(event) => {
                      form.setFieldValue('content', event.currentTarget.value)
                      trackCaret(event)
                    }}
                    onClick={trackCaret}
                    onKeyUp={trackCaret}
                  />
                </div>
              </Menu.Target>
              <Menu.Dropdown
                className="max-h-[300px] overflow-y-auto"
                onFocus={() => contentRef.current?.focus()}
              >
                {mentionQuery !== null &&
                  mentionOptions(mentionQuery).map(({ group, options }) => (
                    <div key={group}>
                      <Menu.Label>{group}</Menu.Label>
                      {options.map((option) => (
                        <Menu.Item
                          key={option.key}
                          onClick={() =>
                            insertAtCursor(option.text, {
                              mention: true,
                              replaceTrigger: true,
                            })
                          }
                        >
                          {option.label}
                        </Menu.Item>
                      ))}
                    </div>
                  ))}
                {mentionQuery !== null &&
                  mentionOptions(mentionQuery).length === 0 && (
                    <Menu.Item disabled>No matches</Menu.Item>
                  )}
              </Menu.Dropdown>
            </StyledMenu>
            <div className="text-xs font-medium text-doubleNickel-gray-600">
              {contentLength}/{NOTE_MAX_LENGTH}
            </div>
          </div>
        </div>
        <Group
          justify="flex-end"
          mt="md"
          className="grid grid-cols-2 gap-4 border-t py-4"
        >
          <Button text="Cancel" variant="outline" onClick={onClose} />
          <Button
            type="submit"
            text={edit ? 'Save' : 'Create'}
            variant="filled"
            loading={creating || updating}
          />
        </Group>
      </form>
    </Modal>
  )
}

export default CreateNoteTemplateModal
